import { api } from './api';
import type { WorkspaceFile } from '@sahaj/types';

export type ResourcePurpose = 'thumbnail' | 'preview' | 'stream' | 'download';

export interface BlobResourceResult {
  blobUrl: string;
  revoke: () => void;
}

class FileResourceService {
  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('sahaj_auth_access_token');
  }

  public getResourceUrl(workspaceId: string, fileId: string, purpose: ResourcePurpose = 'preview'): string {
    const token = this.getToken();
    const downloadUrl = api.files.getDownloadUrl(workspaceId, fileId, token || undefined);
    
    if (purpose === 'download') {
      return `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}disposition=attachment`;
    }
    
    return downloadUrl;
  }

  public async fetchBlobResource(workspaceId: string, fileId: string): Promise<BlobResourceResult> {
    const token = this.getToken();
    const url = this.getResourceUrl(workspaceId, fileId, 'preview');
    
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch file binary resource: HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    return {
      blobUrl,
      revoke: () => URL.revokeObjectURL(blobUrl)
    };
  }

  public canPreview(file: WorkspaceFile): boolean {
    const mime = (file.fileType || '').toLowerCase();
    const cat = file.fileCategory;
    const name = (file.fileName || '').toLowerCase();

    if (cat === 'IMAGE' || mime.startsWith('image/')) return true;
    if (cat === 'VIDEO' || mime.startsWith('video/')) return true;
    if (cat === 'AUDIO' || mime.startsWith('audio/')) return true;
    if (mime === 'application/pdf' || name.endsWith('.pdf')) return true;
    if (mime.startsWith('text/') || name.endsWith('.txt') || name.endsWith('.csv') || name.endsWith('.json') || name.endsWith('.md')) return true;

    return false;
  }
}

export const fileResourceService = new FileResourceService();
